import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import mixpanel from 'mixpanel-browser';
import { jwtDecode } from 'jwt-decode';

import { get, set } from './global.service';

const MAX_RETRY_ATTEMPTS = 200;

@Injectable({ providedIn: 'root' })
export class ConfigService {
    token: string;
    private hubDataPromise: Promise<void> | null = null;
    private refreshTimer: ReturnType<typeof setTimeout> | null = null;
    private failureCount = 0;

    constructor(
        private httpClient: HttpClient //
    ) { }

    loadConfig(): Promise<void> {
        return new Promise(resolve => {
            const baseUrl = '.';
            this.httpClient.get(`${baseUrl}/assets/JSONs/app.config.json`).subscribe({
                next: (resp: any) => {
                    Object.keys(resp).forEach(key => set(key, resp[key]));
                    set('baseUrl', baseUrl);
                    resolve();
                },
                error: (err: any) => {
                    console.error(err);
                    resolve();
                }
            });
        });
    }

    isSignalRTokenValid(): boolean {
        const token = get('signalRAccessToken');
        if (!token) return false;
        try {
            const decoded = jwtDecode(token);
            // Consider invalid if it expires within 60 seconds
            return (decoded?.exp ?? 0) > Math.floor(Date.now() / 1000) + 60;
        } catch {
            return false;
        }
    }

    async ensureValidSignalRToken(): Promise<void> {
        if (!this.isSignalRTokenValid()) {
            await this.loadHubData();
        }
    }

    loadHubData(): Promise<void> {
        if (this.hubDataPromise) return this.hubDataPromise;

        this.hubDataPromise = new Promise<void>(resolve => {
            this.httpClient.post(`${get('hubSignalrUrl')}`, null, { headers: { 'Access-Control-Allow-Origin': '*' } }).subscribe({
                next: (resp: any) => {
                    set('signalRUrl', resp.Url);
                    set('signalRAccessToken', resp.AccessToken);
                    this.failureCount = 0;
                    const delay = (jwtDecode(resp.AccessToken)?.exp - Math.floor(Date.now() / 1000) - 300) * 1000;
                    this.scheduleRefresh(delay);
                    this.hubDataPromise = null;
                    resolve();
                },
                error: () => {
                    this.failureCount++;
                    if (this.failureCount < MAX_RETRY_ATTEMPTS) {
                        this.scheduleRefresh(10000);
                    } else {
                        console.warn(`loadHubData: giving up after ${MAX_RETRY_ATTEMPTS} failed attempts`);
                    }
                    this.hubDataPromise = null;
                    resolve();
                }
            });
        });

        return this.hubDataPromise;
    }

    private scheduleRefresh(delayMs: number): void {
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }
        if (delayMs <= 0) return;
        this.refreshTimer = setTimeout(() => {
            this.refreshTimer = null;
            this.loadHubData();
        }, delayMs);
    }

    mixPanelInit(): Promise<void> {
        return new Promise((resolve) => {
            const token = get('mixPanelToken');
            if (token) mixpanel.init(token);
            resolve();
        });

    }
}
